import { useState, useEffect, useMemo } from "react";
import { Bell, User, LogOut, Settings, ChevronRight, Home, X, Loader2, UserCog, UserCheck, HandCoins, Edit, Upload, Bot, FileBarChart, BookmarkCheck, Check, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  notificationService,
  Notification,
} from "@/services/notificationService";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface BreadcrumbItem {
  label: string;
  path: string;
  id: string;
}

const SIDEBAR_SOURCE_KEY = "breadcrumb:sidebarSource";

function getSidebarSource(): { label: string; path: string } | null {
  try {
    const raw = sessionStorage.getItem(SIDEBAR_SOURCE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getRouteDisplayName(path: string): string {

  const exactNames: Record<string, string> = {
    "/dashboard": "Dashboard",
    "/deals": "Deals",
    "/ai-insights": "AI Insights",
    "/document-upload": "Document Upload",
    "/ai-review": "AI Review",
    "/report-generation": "Report Generation",
    "/documents": "Documents",
    "/settings": "Settings",
    "/notifications": "Notifications",
    "/api-test": "API Test",
    "/change-password": "Change Password",
    "/register-deal": "Create Deal",
    "/deals/new": "New Deal",
  };

  if (exactNames[path]) return exactNames[path];

  // dynamic routes
  if (path.startsWith("/deals/") && path !== "/deals/new") {
    return "Deal Details";
  }

  // fallback: /some-path -> Some-path
  return path.charAt(1).toUpperCase() + path.slice(2);
}
function buildBreadcrumbs(pathname: string): BreadcrumbItem[] {
  // public or marketing routes: return empty
  const publicPaths = new Set([
    "/", "/login", "/signup", "/about", "/features", "/contact", "/forgot-password", "/reset-password",
  ]);
  if (publicPaths.has(pathname)) return [];

  const nowId = () => `${pathname}-${Date.now()}`;

  const source = getSidebarSource(); // { label, path } | null
  const currentLeafLabel = getRouteDisplayName(pathname);

  const crumbs: BreadcrumbItem[] = [];

  const pushCrumb = (label: string, path: string) => {
    crumbs.push({ label, path, id: `${path}-${Date.now()}-${Math.random()}` });
  };

  const isCreateDeal = pathname === "/register-deal" || pathname === "/deals/new";

  if (isCreateDeal) {
    if (source?.label && source?.path) {
      pushCrumb(source.label, source.path);
    } else {
      pushCrumb("Dashboard", "/dashboard");
    }
    pushCrumb(getRouteDisplayName(pathname), pathname);
    return crumbs;
  }

  if (pathname.startsWith("/deals/")) {
    pushCrumb("Deals", "/deals");
    pushCrumb("Deal Details", pathname);
    return crumbs;
  }

  const topLevels = new Set([
    "/dashboard", "/deals", "/ai-insights", "/document-upload", "/ai-review",
    "/report-generation", "/documents", "/settings", "/notifications", "/api-test", "/change-password"
  ]);
  if (topLevels.has(pathname)) {
    pushCrumb(getRouteDisplayName(pathname), pathname);
    return crumbs;
  }

  // Fallback: try to use sidebar source as the parent if it makes sense
  if (source?.label && source?.path && source.path !== pathname) {
    pushCrumb(source.label, source.path);
  }
  pushCrumb(currentLeafLabel, pathname);

  // keep last up to 4
  return crumbs.slice(-4);
}

interface HeaderProps {
  onSidebarToggle?: () => void;
  sidebarCollapsed?: boolean;
}

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const m = window.matchMedia(query);
    const onChange = () => setMatches(m.matches);
    try { m.addEventListener("change", onChange); } catch { m.addListener(onChange as any); }
    return () => {
      try { m.removeEventListener("change", onChange); } catch { m.removeListener(onChange as any); }
    };
  }, [query]);
  return matches;
}

export default function Header({ onSidebarToggle, sidebarCollapsed }: HeaderProps = {}) {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [processingRequest, setProcessingRequest] = useState<string | null>(null);
  const [markingAsRead, setMarkingAsRead] = useState<string | null>(null);

  const breadcrumbHistory = useMemo(() => buildBreadcrumbs(location.pathname), [location.pathname]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const list = await notificationService.getNotifications();
      setNotifications(list);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to load notifications.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      setMarkingAsRead(notificationId);
      await notificationService.markAsRead(notificationId);

      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? { ...notification, status: "READ" as const, readAt: new Date().toISOString() }
            : notification
        )
      );

      toast({
        title: "Success",
        description: "Notification marked as read.",
      });
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      toast({
        title: "Error",
        description: "Failed to mark notification as read. Please try again.",
        variant: "destructive",
      });
    } finally {
      setMarkingAsRead(null);
    }
  };

  const handleApproveRoleChange = async (requestId: string, notificationId: string) => {
    try {
      setProcessingRequest(requestId);
      await notificationService.approveRoleChangeRequest(requestId);

      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? {
                ...notification,
                type: "ROLE_CHANGE_APPROVED",
                title: "Role Change Request Approved",
                message: notification.message.replace("- Pending", "- Approved"),
                metadata: { ...notification.metadata, status: "APPROVED" },
              }
            : notification
        )
      );

      toast({
        title: "Success",
        description: "Role change request approved successfully.",
      });

      await loadNotifications();
    } catch (error) {
      console.error("Failed to approve role change request:", error);
      toast({
        title: "Error",
        description: "Failed to approve role change request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleRejectRoleChange = async (requestId: string, notificationId: string) => {
    try {
      setProcessingRequest(requestId);
      await notificationService.rejectRoleChangeRequest(requestId);

      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? {
                ...notification,
                type: "ROLE_CHANGE_REJECTED",
                title: "Role Change Request Rejected",
                message: notification.message.replace("- Pending", "- Rejected"),
                metadata: { ...notification.metadata, status: "REJECTED" },
              }
            : notification
        )
      );

      toast({
        title: "Success",
        description: "Role change request rejected successfully.",
      });

      await loadNotifications();
    } catch (error) {
      console.error("Failed to reject role change request:", error);
      toast({
        title: "Error",
        description: "Failed to reject role change request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessingRequest(null);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "DEAL_CREATED":
        return HandCoins;
      case "DEAL_UPDATED":
        return Edit;
      case "DOCUMENT_UPLOADED":
        return Upload;
      case "AI_REVIEW_COMPLETED":
        return Bot;
      case "REPORT_GENERATED":
        return FileBarChart;
      case "ROLE_CHANGE_REQUESTED":
        return UserCog;
      case "ROLE_CHANGE_APPROVED":
      case "ROLE_CHANGE_REJECTED":
        return UserCheck;
      default:
        return Bell;
    }
  };

  const getRoleChangeStatus = (notification: Notification) => {
    if (notification.type === "ROLE_CHANGE_APPROVED") {
      return "Approved";
    } else if (notification.type === "ROLE_CHANGE_REJECTED") {
      return "Rejected";
    } else if (notification.type === "ROLE_CHANGE_REQUESTED") {
      return notification.metadata?.status || "Pending";
    }
    return notification.metadata?.status || "Pending";
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const unreadNotifications = notifications.filter(n => n.status === "UNREAD");
  const unreadCount = unreadNotifications.length;

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) { }
  };

  const getUserInitials = (firstName: string, lastName: string) =>
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

  const handleBreadcrumbClick = (path: string) => {
    navigate(path);
  };

  const isAdmin = user?.role === "ADMIN";

  const renderNotificationItem = (n: Notification) => {
    const IconComponent = getNotificationIcon(n.type);
    const isRoleChangeRequest = n.type === "ROLE_CHANGE_REQUESTED";
    const isPending = n.metadata?.status === "PENDING";
    const isUnread = n.status === "UNREAD";

    return (
      <div
        key={n.id}
        className="px-4 py-4 border-b border-border last:border-b-0 hover:bg-accent/30 transition-colors duration-300 ease-out"
      >
        <div className="flex items-start gap-3">
          <div
            className={cn(
              "flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 mt-0.5",
              "transition-all duration-300 ease-out",
              "hover:scale-[1.03]",
              notificationService.getNotificationTypeColor(n.type)
            )}
          >
            <IconComponent className="w-4 h-4" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-semibold text-sm text-foreground leading-snug truncate">
                    {n.title}
                  </p>
                  {isUnread && (
                    <Badge variant="default" className="text-xs px-2 py-0.5 bg-primary/90 flex-shrink-0">
                      New
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
                  {n.message}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {notificationService.getTimeAgo(n.createdAt)}
                </p>

                {(n.type === "ROLE_CHANGE_REQUESTED" ||
                  n.type === "ROLE_CHANGE_APPROVED" ||
                  n.type === "ROLE_CHANGE_REJECTED") && (
                  <div className="text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1 mt-2 inline-block">
                    <div className="flex items-center gap-1">
                      <UserCog className="w-3 h-3" />
                      <span>Status: {getRoleChangeStatus(n)}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-1 flex-shrink-0 ml-2">
                {isUnread && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => handleMarkAsRead(n.id)}
                          disabled={markingAsRead === n.id}
                          className="h-7 w-7 p-0 rounded-full hover:bg-accent/50 transition-colors duration-300 ease-out flex items-center justify-center"
                        >
                          {markingAsRead === n.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <BookmarkCheck className="w-3 h-3 text-primary" />
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Mark as read</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {isAdmin && isRoleChangeRequest && isPending && (
                  <div className="flex gap-1 mt-1">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() =>
                              handleApproveRoleChange(
                                n.metadata?.roleChangeRequestId || "",
                                n.id
                              )
                            }
                            disabled={
                              processingRequest === n.metadata?.roleChangeRequestId ||
                              !n.metadata?.roleChangeRequestId
                            }
                            className="h-7 w-7 rounded-full bg-green-600/20 hover:bg-green-600/40 text-green-600 transition-colors duration-300 ease-out flex items-center justify-center"
                          >
                            {processingRequest === n.metadata?.roleChangeRequestId ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Check className="w-3 h-3" />
                            )}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Accept role change</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() =>
                              handleRejectRoleChange(
                                n.metadata?.roleChangeRequestId || "",
                                n.id
                              )
                            }
                            disabled={
                              processingRequest === n.metadata?.roleChangeRequestId ||
                              !n.metadata?.roleChangeRequestId
                            }
                            className="h-7 w-7 rounded-full bg-red-600/20 hover:bg-red-600/40 text-red-600 transition-colors duration-300 ease-out flex items-center justify-center"
                          >
                            {processingRequest === n.metadata?.roleChangeRequestId ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <X className="w-3 h-3" />
                            )}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Reject role change</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
<header className="w-full border-b border-border px-2 sm:px-4 lg:px-6 py-3 sm:py-4 flex items-center justify-between sticky top-0 z-40 shadow-sm backdrop-blur-sm bg-background/95">
  <div className="flex items-center min-w-0 flex-1 mr-2 sm:mr-4">
    {/* Mobile menu button */}
    {isMobile && onSidebarToggle && (
      <Button
        variant="ghost"
        size="sm"
        onClick={onSidebarToggle}
        className="mr-2 h-9 w-9 p-0 md:hidden hover:bg-accent"
        aria-label="Toggle sidebar"
      >
        <Menu className="h-5 w-5" />
      </Button>
    )}
    {breadcrumbHistory.length > 0 && (
      <nav aria-label="Breadcrumb" className="flex items-center space-x-1 text-sm font-medium overflow-hidden">
        {breadcrumbHistory[0]?.path === '/dashboard' && (
          <div className="flex items-center flex-shrink-0">
            <Home className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground mr-1 sm:mr-2" />
          </div>
        )}

        {breadcrumbHistory.map((crumb, index) => (
          <div key={crumb.id} className="flex items-center min-w-0">
            {index > 0 && (
              <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground mx-0.5 sm:mx-1 flex-shrink-0 transition-transform duration-300 ease-out group-hover:translate-x-0.5" />
            )}

            {index === breadcrumbHistory.length - 1 ? (
              <span className="text-foreground font-semibold px-1 sm:px-2 py-1 rounded-md bg-accent/30 truncate max-w-[80px] sm:max-w-[120px] lg:max-w-[220px] text-xs sm:text-sm">
                {crumb.label}
              </span>
            ) : (
              <button
                onClick={() => handleBreadcrumbClick(crumb.path)}
                className="text-muted-foreground hover:text-foreground transition-colors duration-300 ease-out px-1 sm:px-2 py-1 rounded-md hover:bg-accent/50 truncate max-w-[60px] sm:max-w-[100px] lg:max-w-[180px] text-left text-xs sm:text-sm"
                title={crumb.label}
              >
                {crumb.label}
              </button>
            )}
          </div>
        ))}
      </nav>
    )}
  </div>

  <div className="flex items-center gap-1 sm:gap-2 lg:gap-3 flex-shrink-0">
    <DropdownMenu open={notificationsOpen} onOpenChange={setNotificationsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9 sm:h-10 sm:w-10 rounded-full hover:bg-accent/50 transition-colors duration-300 ease-out overflow-visible"
        >
          <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 sm:h-5 sm:w-5 items-center justify-center rounded-full bg-destructive text-white text-[10px] sm:text-[11px] font-medium shadow-lg animate-pulse">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-72 sm:w-80 lg:w-96 p-0 shadow-xl rounded-xl border border-border bg-background/95 backdrop-blur-sm max-h-[80vh] flex flex-col"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-accent/20 rounded-t-xl flex-shrink-0">
          <span className="text-base font-semibold text-foreground">Notifications</span>
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors h-8"
            onClick={loadNotifications}
            disabled={loading}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
          </Button>
        </div>

        <Tabs defaultValue="all" className="w-full flex flex-col min-h-0">
          <TabsList className="w-full grid grid-cols-2 bg-muted/30 m-2 rounded-lg flex-shrink-0">
            <TabsTrigger value="all" className="text-sm font-medium">
              All ({notifications.length})
            </TabsTrigger>
            <TabsTrigger value="unread" className="text-sm font-medium">
              Unread ({unreadCount})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="flex-1 min-h-0">
            <div className="max-h-80 sm:max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                  <p className="text-sm text-muted-foreground">No notifications yet</p>
                </div>
              ) : (
                notifications.map(renderNotificationItem)
              )}
            </div>
          </TabsContent>

          <TabsContent value="unread" className="flex-1 min-h-0">
            <div className="max-h-80 sm:max-h-96 overflow-y-auto">
              {unreadNotifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                  <p className="text-sm text-muted-foreground">No unread notifications</p>
                </div>
              ) : (
                unreadNotifications.map(renderNotificationItem)
              )}
            </div>
          </TabsContent>
        </Tabs>

        <div className="p-3 border-t border-border bg-accent/10 rounded-b-xl flex-shrink-0">
          <Button className="w-full font-medium" size="sm" variant="secondary" onClick={() => setNotificationsOpen(false)} asChild>
            <Link to="/notifications">View all notifications</Link>
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>

    <Button
      variant="ghost"
      size="icon"
      className="h-9 w-9 sm:h-10 sm:w-10 rounded-full hover:bg-accent/50 transition-colors duration-300 ease-out"
      asChild
    >
      <Link to="/settings">
        <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
      </Link>
    </Button>

    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="flex items-center gap-2 p-1 hover:bg-accent/50 rounded-full transition-colors duration-300 ease-out">
          <Avatar className="h-8 w-8 sm:h-9 sm:w-9 ring-2 ring-accent/50">
            <AvatarFallback className="text-xs sm:text-sm font-semibold bg-gradient-to-br from-primary/20 to-accent/40">
              {user ? getUserInitials(user.firstName, user.lastName) : 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="hidden md:block text-left">
            <p className="text-sm font-medium">{user ? `${user.firstName} ${user.lastName}` : 'User'}</p>
            <p className="text-xs text-muted-foreground">Account Settings</p>
          </div>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-48 sm:w-56 lg:w-64 p-2 shadow-xl rounded-xl border border-border bg-background/95 backdrop-blur-sm">
        <DropdownMenuLabel className="text-base font-semibold pb-2 px-2 truncate">
          {user ? `${user.firstName} ${user.lastName}` : 'User'}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="my-1" />
        <DropdownMenuItem
          onClick={() => (window.location.href = '/settings')}
          className="text-sm py-3 cursor-pointer focus:bg-accent/50 rounded-lg transition-colors duration-300 ease-out"
        >
          <User className="mr-3 h-4 w-4" /> Profile Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator className="my-1" />
        <DropdownMenuItem
          onClick={handleLogout}
          className="text-sm py-3 text-destructive cursor-pointer focus:bg-destructive/10 rounded-lg transition-colors duration-300 ease-out"
        >
          <LogOut className="mr-3 h-4 w-4" /> Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
</header>
  );
}
